import { useParams } from 'next/navigation';

import { useState } from 'react';

import classNames from 'classnames';
import DOMPurify from 'isomorphic-dompurify';

import { useModalController } from '@uikit';

import type {
  LaunchpadSalePool} from '@api';
import {
  LAUNCHPAD_SALE_POOL_STATUS,
  LAUNCHPAD_SALE_POOL_WHITELIST_STRATEGY
} from '@api';

import { AuthWeb3Modal } from '@shared/common/components/modals/AuthWeb3Modal';
import { CompleteYourMagicIdModal } from '@shared/common/components/modals/CompleteYourMagicIdModal';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { getPoolInfo } from '@shared/helpers/getPoolInfo';
import { useClampText } from '@shared/hooks/useClampText';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';

import { useSelfPoolAllocationQuery } from '@query/launchpad';

import PortfolioPoolWhitelistRequirementsModal from '@launchpad/components/pages/Portfolio/ui/PortfolioPool/PortfolioPoolWhitelistRequirementsModal';
import useSelfDepositedAmountQuery from '@launchpad/hooks/useSelfDepositedAmountQuery';
import { useSubscribeToSaleReminderMutation } from '@launchpad/hooks/useSubscribeToSaleReminder';
import { MarkCheck } from '@ui/uikit/components/icons/mono/MarkCheck';

import DepositButton from './DepositButton';
import PoolStatList from './PoolStatList';
import { usePoolStats } from './usePoolStats';

const PoolCard = ({
  pool,
  token,
  projectSlug,
  className,
}: {
  pool: LaunchpadSalePool;
  token: string;
  className?: string;
  projectSlug: string;
}) => {
  const { id } = useParams<{ id: string }>();
  const [isOpenEVMModal, setIsOpenEVMModal] = useState(false);
  const [isOpenMagicIDModal, setIsOpenMagicIDModal] = useState(false);

  const { isAuth, user } = useAuth();
  const emailFromMagicId = user?.attributes.email || '';
  const isSqrpBasedOffchainPool = pool?.attributes?.sqrpBasedOffchainTransactions;

  const { data: selfDepositInfo } = useSelfDepositedAmountQuery({
    depositContractAddress: pool?.attributes?.depositContractAddress,
    type: pool?.attributes?.type,
    enabled: !isSqrpBasedOffchainPool,
  });

  const { data: poolAllocation } = useSelfPoolAllocationQuery({
    poolId: pool.id,
    poolType: pool?.attributes?.type,
  });

  const selfDeposited =
    (isSqrpBasedOffchainPool ? poolAllocation?.amountBought : selfDepositInfo?.baseDeposited) || 0;
  const whitelistRequirementsModal = useModalController();

  const descriptionClamp = useClampText<HTMLParagraphElement>({ lines: 4 });

  const poolInfo = getPoolInfo(pool);
  const stats = usePoolStats({
    pool,
    token,
    depositedAmount: selfDeposited,
    fee: selfDepositInfo?.fee,
    isSqrpBasedOffchainPool,
  });

  const showWhitelistApproved =
    pool.attributes?.meta?.whiteListExists &&
    pool.attributes.status === LAUNCHPAD_SALE_POOL_STATUS.ANNOUNCED &&
    pool.attributes?.meta?.isWhitelistParticipant;

  const showReminderSet =
    pool.attributes.status === LAUNCHPAD_SALE_POOL_STATUS.ANNOUNCED &&
    pool.attributes?.meta?.isUserSubscribed;

  const featureToggles = useFeatureToggles();
  const showSaveMySpotButton = !showReminderSet && featureToggles.showSaveMySpotButton;

  const { mutateAsync: subscribeToSaleReminder } = useSubscribeToSaleReminderMutation(pool.id, id);

  const handleSubscribe = () => {
    if (!isAuth) {
      setIsOpenEVMModal(true);
    } else {
      if (!emailFromMagicId) {
        setIsOpenMagicIDModal(true);
      } else {
        void subscribeToSaleReminder();
      }
    }
  };

  const updateModalState = async (isOpen: boolean) => {
    setIsOpenMagicIDModal(isOpen);
  };

  const onSubmit = () => {
    updateModalState(false);
  };

  return (
    <div
      className={classNames(
        'py-6 px-4 rounded-lg border flex flex-col border-primary-200 min-h-[30.25rem] overflow-auto',
        className,
      )}
      data-qa={pool.attributes.name}
    >
      <div className="mb-2 flex justify-between items-center">
        <h4 className="text-2xl font-semibold truncate leading-9">{pool.attributes.name}</h4>
      </div>

      {poolInfo.description && (
        <div className="h-[5.8rem] relative border-y bg-neutral-50 border-primary-50 pb-1 leading-5">
          <div
            className={classNames('bg-neutral-50 p-[12px] pb-1 m-[-12px] flex flex-col', {
              'border-b border-primary-100': descriptionClamp.isExpanded,
            })}
          >
            <p
              className={classNames('inner-html mt-1 break-words', {
                'line-clamp-3': descriptionClamp.isCollapsed,
              })}
              ref={descriptionClamp.ref}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(poolInfo.description) }}
            />
            {descriptionClamp.canClamp ? (
              <button
                className="font-medium self-end button button-ghost underline text-primary flex-shrink-0 text-sm bg-transparent p-0 m-0 min-h-0"
                onClick={() => descriptionClamp.toggle()}
              >
                {descriptionClamp.isClamped ? 'Show More' : 'Show Less'}
              </button>
            ) : null}
          </div>
        </div>
      )}

      <PoolStatList items={stats} className="mb-2" />

      {showWhitelistApproved && (
        <p className="w-full flex flex-1 min-h-10 max-h-10 mb-2 mt-auto justify-center items-center text-center font-semibold rounded-xl bg-green-50 text-sm gap-2">
          <MarkCheck className="size-[16px] text-base-text-green" />
          <span>Whitelist Approved</span>
        </p>
      )}

      <div className={classNames('w-full', { 'mt-auto': !showWhitelistApproved })}>
        <DepositButton
          projectSlug={projectSlug}
          userDeposit={selfDeposited}
          pool={pool}
          className="w-full"
          onJoinWhitelist={() => whitelistRequirementsModal.open()}
          onSubscribe={handleSubscribe}
          showSaveMySpotButton={showSaveMySpotButton}
        />
      </div>

      {showReminderSet && (
        <p className="w-full flex flex-1 min-h-10 max-h-10 mt-2 justify-center items-center text-center font-semibold rounded-xl bg-green-50 text-green-700 text-sm">
          Reminder Set
        </p>
      )}

      <PortfolioPoolWhitelistRequirementsModal
        disabled={Boolean(
          pool.whiteList?.attributes.criteriaCheckStrategy ===
            LAUNCHPAD_SALE_POOL_WHITELIST_STRATEGY.NONE,
        )}
        poolId={pool.id}
        projectId={projectSlug}
        {...whitelistRequirementsModal.props}
      />

      <AuthWeb3Modal isOpen={isOpenEVMModal} setIsOpen={setIsOpenEVMModal} />

      <CompleteYourMagicIdModal
        isOpenMagicIDModal={isOpenMagicIDModal}
        onSubmit={onSubmit}
        updateModalState={updateModalState}
        showEmailInputOnly
      />
    </div>
  );
};

export default PoolCard;
